import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
// form
import { useForm } from 'react-hook-form';
// material
import { Box, Card, Container, Stack, Typography, styled, InputLabel } from '@mui/material';
// GRAPHQL
import { useQuery } from '@apollo/client';
import { ProfileGQLFunction } from '../graphql/SEVER_PROFILE';

// hooks
import useResponsive from '../hooks/useResponsive';
// components
import Page from '../components/Page';
import { FormProvider, RHFTextField } from '../components/hook-form';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center'
}));
const ContentStyle = styled(Card)(({ theme }) => ({
  width: '100%',
  margin: theme.spacing(2),
  display: 'flex',
  justifyContent: 'flex-end',
  flexDirection: 'column',
  padding: theme.spacing(2)
}));

export default function ServerProfile() {
  const { t } = useTranslation();
  const smUp = useResponsive('up', 'sm');
  const { enqueueSnackbar } = useSnackbar();

  const { data: queryData, loading: queryLoading } = useQuery(ProfileGQLFunction.GET_SERVER_PROFILEPO, {
    fetchPolicy: 'network-only'
  });

  const defaultValues = {
    cn: '',
    o: '',
    domain: '',
    apiServerHttp: '',
    apiServerGrpc: '',
    fileServerHttp: '',
    backupServerGrpc: '',
    msgRetention: '',
    fileRetention: '',
    inActiveUserRetention: '',
    desktopUpdateLink: '',
    themeColor: '',
    themeIcon: ''
  };

  const methods = useForm({
    defaultValues
  });

  const { reset } = methods;

  const formatUrl = (host, port, includeProto = true) => {
    if (port === 443 || port === '443') {
      return `https://${host}`;
    }
    const proto = includeProto ? 'http://' : '';
    return `${proto}${host}:${port}`;
  };

  useEffect(() => {
    if (queryData) {
      const { getServerProfile } = queryData;
      reset({
        cn: getServerProfile.cn,
        o: getServerProfile.o,
        domain: getServerProfile.domain,
        apiServerHttp: formatUrl(getServerProfile.domainProfile.apiServerHttp.host, getServerProfile.domainProfile.apiServerHttp.port),
        apiServerGrpc: formatUrl(getServerProfile.domainProfile.apiServerGrpc.host, getServerProfile.domainProfile.apiServerGrpc.port, false),
        fileServerHttp: formatUrl(getServerProfile.domainProfile.fileServerHttp.host, getServerProfile.domainProfile.fileServerHttp.port),
        backupServerGrpc: formatUrl(getServerProfile.domainProfile.backupServerGrpc.host, getServerProfile.domainProfile.backupServerGrpc.port, false),
        msgRetention: getServerProfile.msgRetention,
        fileRetention: getServerProfile.fileRetention,
        inActiveUserRetention: getServerProfile.inActiveUserRetention,
        desktopUpdateLink: getServerProfile.desktopUpdateLink,
        themeColor: getServerProfile.themeColor,
        themeIcon: getServerProfile.themeIcon
      });
    }
  }, [queryData, reset]);

  return (
    <Page title={t('profile.title')}>
      <Container maxWidth={false}>
        <Stack direction="column">
          <Typography variant="h4" gutterBottom>
            {t('profile.title')}
          </Typography>
        </Stack>

        <RootStyle>
          <ContentStyle>
            <FormProvider
              methods={methods}
              onSubmit={
                /*handleSubmit(onSubmit)*/
                console.log('server profile is read-only')
              }
            >
              <Box
                sx={{
                  display: 'grid',
                  columnGap: 2,
                  rowGap: 3,
                  gridTemplateColumns: smUp ? 'repeat(1, 1fr)' : '1fr',
                  mt: 1,
                  mb: 4
                }}
              >
                {/* <Skeleton variant="rounded" /> */}
                <Box>
                  <InputLabel shrink>{t('profile.info')}</InputLabel>
                </Box>
                <RHFTextField name="cn" label={t('profile.name')} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
                <RHFTextField name="domain" label={t('profile.domain')} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
                <RHFTextField name="o" label={t('profile.org')} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
                <Box>
                  <InputLabel shrink>{t('profile.conn')}</InputLabel>
                </Box>
                <RHFTextField name="apiServerHttp" label={t('profile.api_http')} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
                <RHFTextField name="apiServerGrpc" label={t('profile.api_grpc')} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
                <RHFTextField name="fileServerHttp" label={t('profile.file_http')} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
                <RHFTextField name="backupServerGrpc" label={t('profile.backup_grpc')} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
                <Box>
                  <InputLabel shrink>{t('profile.retention')}</InputLabel>
                </Box>
                <RHFTextField
                  name="msgRetention"
                  label={t('profile.msg_retention')}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
                <RHFTextField
                  name="fileRetention"
                  label={t('profile.file_retention')}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
                <RHFTextField
                  name="inActiveUserRetention"
                  label={t('profile.user_retention')}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
                <Box>
                  <InputLabel shrink>{t('profile.additional')}</InputLabel>
                </Box>
                <RHFTextField
                  name="desktopUpdateLink"
                  label={t('profile.update_link')}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
                <RHFTextField name="themeColor" label={t('profile.theme_color')} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
                <RHFTextField name="themeIcon" label={t('profile.theme_icon')} InputProps={{ readOnly: true }} InputLabelProps={{ shrink: true }} />
              </Box>
            </FormProvider>
          </ContentStyle>
        </RootStyle>
      </Container>
    </Page>
  );
}
